import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, authHeaders } from "../services/config";
import "../pages/PrestacionesPage.css";

const fmt = (n) => (n != null && n !== "" ? `$${parseFloat(n).toFixed(0)}` : "—");
const fmtMin = (m) => {
  if (!m) return "—";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return min ? `${h}h ${min}min` : `${h}h`;
};

const emptyServicio = { nombre: "", descripcion: "", costo_base: "", duracion_minutos: "" };

export default function ProfesionalPrestaciones({ profesionalId, profesional }) {
  const navigate = useNavigate();
  const profSel = String(profesionalId || "");

  const [profesionales, setProfesionales] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  const [modal, setModal] = useState({ open: false, edit: false, data: emptyServicio });
  const [saving, setSaving] = useState(false);
  const [errorModal, setErrorModal] = useState(null);

  const [copyOpen, setCopyOpen] = useState(false);
  const [copyOrigenId, setCopyOrigenId] = useState("");
  const [copying, setCopying] = useState(false);
  const [errorCopy, setErrorCopy] = useState(null);

  const fetchProfesionales = useCallback(async () => {
    try {
      const res = await fetch(`${API}/profesionales`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setProfesionales(data.data.filter((p) => p.activo));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchServicios = useCallback(async (profId) => {
    if (!profId) {
      setServicios([]);
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${API}/profesionales/${profId}/practicas`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setServicios(data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfesionales();
  }, [fetchProfesionales]);

  useEffect(() => {
    fetchServicios(profSel);
  }, [profSel, fetchServicios]);

  const abrirModal = (servicio = null) => {
    setErrorModal(null);
    setModal({
      open: true,
      edit: !!servicio,
      data: servicio ? { ...servicio } : { ...emptyServicio },
    });
  };

  const cerrarModal = () => setModal({ open: false, edit: false, data: emptyServicio });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setModal((prev) => ({ ...prev, data: { ...prev.data, [name]: value } }));
  };

  const guardar = async () => {
    if (!modal.data.nombre.trim()) {
      setErrorModal("El nombre es requerido");
      return;
    }
    try {
      setSaving(true);
      const url = modal.edit
        ? `${API}/profesionales/${profSel}/practicas/${modal.data.id}`
        : `${API}/profesionales/${profSel}/practicas`;
      const method = modal.edit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: modal.data.nombre,
          descripcion: modal.data.descripcion || null,
          costo_base: modal.data.costo_base || null,
          duracion_minutos: modal.data.duracion_minutos || null,
        }),
      });
      const result = await res.json();
      if (result.success) {
        await fetchServicios(profSel);
        cerrarModal();
      } else {
        setErrorModal(result.message || "Error al guardar");
      }
    } catch {
      setErrorModal("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este servicio?")) return;
    try {
      await fetch(`${API}/profesionales/${profSel}/practicas/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      await fetchServicios(profSel);
    } catch (e) {
      console.error(e);
    }
  };

  const copiar = async () => {
    if (!copyOrigenId) {
      setErrorCopy("Seleccioná un profesional origen");
      return;
    }
    if (String(copyOrigenId) === String(profSel)) {
      setErrorCopy("El origen no puede ser el mismo que el destino");
      return;
    }
    try {
      setCopying(true);
      const res = await fetch(
        `${API}/profesionales/${profSel}/practicas/copiar-desde/${copyOrigenId}`,
        { method: "POST", headers: authHeaders() }
      );
      const data = await res.json();
      if (data.success) {
        await fetchServicios(profSel);
        setCopyOpen(false);
        setCopyOrigenId("");
        setErrorCopy(null);
      } else {
        setErrorCopy(data.message || "No se pudo copiar");
      }
    } catch {
      setErrorCopy("Error de conexión");
    } finally {
      setCopying(false);
    }
  };

  const profInfo =
    profesional ||
    profesionales.find((p) => String(p.id) === profSel);

  const serviciosFiltrados = servicios.filter((s) =>
    s.nombre.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <>
      <button type="button" className="sp-btn-back" onClick={() => navigate("/profesionales")}>
        ← Volver
      </button>

      <div className="sp-header">
        <div>
          <h1 className="sp-title">Prestaciones</h1>
          {profInfo && (
            <p className="sp-subtitle sp-subtitle-prof">
              <span
                className="sp-prof-dot"
                style={{ background: profInfo.color || "var(--color-primary-900, #032548)" }}
              />
              {profInfo.apellido}, {profInfo.nombre}
            </p>
          )}
          {!profInfo && (
            <p className="sp-subtitle">Servicios y precios del profesional</p>
          )}
        </div>
      </div>

      <div className="sp-tab-content">
        <div className="sp-toolbar">
          <div className="sp-search-wrap">
            <span aria-hidden="true">🔍</span>
            <input
              type="text"
              placeholder="Buscar servicio..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="sp-search"
            />
          </div>
          <div className="sp-toolbar-actions">
            <button type="button" className="sp-btn-nuevo" onClick={() => abrirModal()}>
              + Nuevo servicio
            </button>
            <button
              type="button"
              className="sp-btn-copy"
              onClick={() => {
                setErrorCopy(null);
                setCopyOrigenId("");
                setCopyOpen(true);
              }}
            >
              Copiar desde otro prof.
            </button>
          </div>
        </div>

        {loading ? (
          <div className="sp-loading">
            <div className="sp-spinner" />
            <p>Cargando…</p>
          </div>
        ) : serviciosFiltrados.length === 0 ? (
          <div className="sp-empty">
            <span aria-hidden="true">💊</span>
            <p>
              {busqueda
                ? "Sin resultados para esa búsqueda"
                : "No hay servicios configurados. Creá el primero o copiá desde otro profesional."}
            </p>
          </div>
        ) : (
          <div className="sp-grid">
            {serviciosFiltrados.map((s) => (
              <div key={s.id} className="sp-card">
                <div className="sp-card-top">
                  <div className="sp-card-icon">{s.nombre[0]?.toUpperCase() || "S"}</div>
                  <div className="sp-card-info">
                    <p className="sp-card-nombre">{s.nombre}</p>
                    {s.descripcion && <p className="sp-card-desc">{s.descripcion}</p>}
                  </div>
                </div>
                <div className="sp-card-badges">
                  <span className="sp-badge precio">💵 {fmt(s.costo_base)}</span>
                  <span className="sp-badge tiempo">⏱ {fmtMin(s.duracion_minutos)}</span>
                </div>
                <div className="sp-card-actions">
                  <button type="button" className="sp-btn-edit" onClick={() => abrirModal(s)}>
                    ✏️ Editar
                  </button>
                  <button type="button" className="sp-btn-del" onClick={() => eliminar(s.id)}>
                    🗑 Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal.open && (
        <div className="sp-overlay" onClick={cerrarModal}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <h3>{modal.edit ? "✏️ Editar servicio" : "➕ Nuevo servicio"}</h3>
              <button type="button" className="sp-modal-close" onClick={cerrarModal}>
                ✕
              </button>
            </div>

            {errorModal && <div className="sp-error">{errorModal}</div>}

            <div className="sp-modal-body">
              <label className="sp-label">Nombre *</label>
              <input
                className="sp-input"
                name="nombre"
                placeholder="Ej: Limpieza dental, Consulta..."
                value={modal.data.nombre}
                onChange={handleChange}
                autoFocus
              />

              <label className="sp-label">Descripción</label>
              <textarea
                className="sp-input sp-textarea"
                name="descripcion"
                placeholder="Descripción opcional..."
                value={modal.data.descripcion || ""}
                onChange={handleChange}
                rows={2}
              />

              <div className="sp-row-2">
                <div>
                  <label className="sp-label">Precio ($)</label>
                  <input
                    className="sp-input"
                    type="number"
                    name="costo_base"
                    placeholder="0"
                    min="0"
                    step="0.01"
                    value={modal.data.costo_base || ""}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="sp-label">Duración (minutos)</label>
                  <input
                    className="sp-input"
                    type="number"
                    name="duracion_minutos"
                    placeholder="Ej: 30, 45, 60…"
                    min="1"
                    max="480"
                    value={modal.data.duracion_minutos || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {modal.data.duracion_minutos && (
                <p className="sp-hint">⏱ Equivale a {fmtMin(parseInt(modal.data.duracion_minutos, 10))}</p>
              )}
            </div>

            <div className="sp-modal-footer">
              <button type="button" className="sp-btn-cancel" onClick={cerrarModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="sp-btn-save" onClick={guardar} disabled={saving}>
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {copyOpen && (
        <div className="sp-overlay" onClick={() => setCopyOpen(false)}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <h3>Copiar servicios desde otro profesional</h3>
              <button type="button" className="sp-modal-close" onClick={() => setCopyOpen(false)}>
                ✕
              </button>
            </div>

            {errorCopy && <div className="sp-error">{errorCopy}</div>}

            <div className="sp-modal-body">
              <label className="sp-label">Profesional origen *</label>
              <select
                className="sp-input"
                value={copyOrigenId}
                onChange={(e) => setCopyOrigenId(e.target.value)}
              >
                <option value="">— Seleccioná profesional origen —</option>
                {profesionales
                  .filter((p) => String(p.id) !== profSel)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.apellido}, {p.nombre}
                    </option>
                  ))}
              </select>
              <p className="sp-hint">
                Solo se copian los servicios que no existan todavía en la lista (por nombre).
              </p>
            </div>

            <div className="sp-modal-footer">
              <button
                type="button"
                className="sp-btn-cancel"
                onClick={() => setCopyOpen(false)}
                disabled={copying}
              >
                Cancelar
              </button>
              <button type="button" className="sp-btn-save" onClick={copiar} disabled={copying}>
                {copying ? "Copiando…" : "Copiar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
